import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { parseScheduleJson } from '../../../../lib/workSchedule'
import type { DaySchedule } from '../../../../lib/workSchedule'
import type { TurfContactDto } from '../../../../types/canvasser'

const DAYS: [string, string][] = [
  ['monday', 'Mon'], ['tuesday', 'Tue'], ['wednesday', 'Wed'], ['thursday', 'Thu'],
  ['friday', 'Fri'], ['saturday', 'Sat'], ['sunday', 'Sun'],
]

/** A label/value line; renders nothing without a value. Shared with DetailsSection. */
export function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <HStack justify="space-between" gap={3}>
      <Text fontSize="sm" color="fg.muted">{label}</Text>
      <Text fontSize="sm" fontWeight="medium" textAlign="right">{value}</Text>
    </HStack>
  )
}

/** [Spec §15.1 I1, P5-7] Where this person works. Hours are a hint, never a promise: "may vary". */
export function WorkSection({ person }: { person: TurfContactDto }) {
  const hasWork = !!(person.workplaceLabel || person.workStreetAddress || person.workDepartment || person.workPosition || person.workScheduleJson)
  if (!hasWork) return null
  let hours: [string, DaySchedule][] = []
  try {
    const s = person.workScheduleJson ? parseScheduleJson(person.workScheduleJson) : {}
    hours = DAYS.flatMap(([k, short]) => (s[k] ? [[short, s[k]!] as [string, DaySchedule]] : []))
  } catch { hours = [] }
  const place = [person.workCity, person.workZip].filter(Boolean).join(' ')
  return (
    <VStack align="stretch" gap={2} p={3} borderWidth="1px" borderColor="border" borderRadius="md">
      <Text fontWeight="semibold">Work</Text>
      {person.workplaceLabel && <Text fontWeight="medium">{person.workplaceLabel}</Text>}
      {person.workStreetAddress && <Text fontSize="sm">{person.workStreetAddress}</Text>}
      {place && <Text fontSize="sm" color="fg.muted">{place}</Text>}
      <InfoRow label="Department" value={person.workDepartment} />
      <InfoRow label="Job title" value={person.workPosition} />
      {hours.length > 0 && (
        <Box>
          <Text fontSize="sm" color="fg.muted" mb={1}>Work hours (may vary)</Text>
          {hours.map(([day, h]) => <Text key={day} fontSize="sm">{`${day} ${h.start}–${h.end}`}</Text>)}
        </Box>
      )}
    </VStack>
  )
}
